import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getBrandingPublico, BrandingConfig } from "../services/branding";
import { anexoUrlAbsoluto } from "../services/tarefas";

/** Chave do localStorage onde guardamos o último branding válido. main.tsx
 *  lê isso pra aplicar antes do React montar e evitar flash. */
export const BRANDING_CACHE_KEY = "recdc_branding_cache";

/**
 * Carrega o branding público e aplica:
 * - <title> e document.title
 * - favicon dinâmico
 * - variáveis CSS de paleta primária (--color-primary) e secundária (--color-secondary-50..900)
 *   A paleta secundária é mixada em runtime a partir de `corSecundaria` e usada pelos
 *   overrides em index.css que pegam classes Tailwind `bg-blue-*`, `text-blue-*` etc.
 */
function lerCacheBranding(): BrandingConfig | undefined {
  try {
    const raw = localStorage.getItem(BRANDING_CACHE_KEY);
    if (!raw) return undefined;
    const obj = JSON.parse(raw);
    if (obj && typeof obj === "object") return obj as BrandingConfig;
  } catch {}
  return undefined;
}

export function useBranding() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["branding"],
    queryFn: getBrandingPublico,
    // Usa o último branding válido como render inicial — evita flash do azul
    // padrão antes do fetch chegar. Atualiza assim que o request retornar.
    initialData: lerCacheBranding,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!data) return;
    aplicarBranding(data);
    try { localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(data)); } catch {}
  }, [data]);

  // Reage a evento de outros tabs/usuário
  useEffect(() => {
    function onChange() { qc.invalidateQueries({ queryKey: ["branding"] }); }
    window.addEventListener("growsoft:branding-atualizado", onChange);
    return () => window.removeEventListener("growsoft:branding-atualizado", onChange);
  }, [qc]);

  return data;
}

const FAVICON_PADRAO = "/favicon.svg";
const COR_SECUNDARIA_PADRAO = "#2563eb"; // blue-600
const COR_CHAT_PADRAO = "#0ea5e9";       // sky-500 — mais suave que o azul de ação

export function aplicarBranding(b: BrandingConfig) {
  if (typeof document === "undefined") return;

  document.title = b.nome || "RECDC OS";
  if (b.corPrimaria) {
    document.documentElement.style.setProperty("--color-primary", b.corPrimaria);
    // NÃO sobrescreve `meta[name="theme-color"]` aqui — esse valor é gerenciado
    // por applyTheme() pra acompanhar o tema light/dark do app (status bar do
    // PWA Android fica preta no dark e branca no light).
  }

  // Paleta secundária — gera tons 50–900 a partir da cor escolhida e aplica
  // como CSS vars usadas pelos overrides globais.
  const corSec = b.corSecundaria || COR_SECUNDARIA_PADRAO;
  aplicarPaleta("secondary", corSec);

  // Paleta exclusiva do chat — usada por classes `bg-chat-*`, `from-chat-*` etc.
  const corChat = b.corChat || COR_CHAT_PADRAO;
  aplicarPaleta("chat", corChat);

  // Favicon: usa o do branding se houver; senão volta pro padrão SVG.
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    link.type = "image/svg+xml";
    document.head.appendChild(link);
  }
  link.href = b.faviconUrl ? anexoUrlAbsoluto(b.faviconUrl) : FAVICON_PADRAO;
}

/**
 * Gera uma paleta tonal a partir de uma cor base hex e seta no :root.
 * Estratégia: trata a cor recebida como o tom "600" (ação principal) e
 * mistura com branco/preto pra produzir os outros tons. Não é colorimetria
 * perfeita, mas resulta em paleta consistente o suficiente pra UI.
 *
 * Setado em CSS vars `--color-${prefixo}-{50..900}` + alias `--color-${prefixo}`.
 */
function aplicarPaleta(prefixo: "secondary" | "chat", hex: string) {
  const rgb = parseHex(hex);
  if (!rgb) return;
  const palette: Record<string, string> = {
    "50":  mix(rgb, [255, 255, 255], 0.92),
    "100": mix(rgb, [255, 255, 255], 0.82),
    "200": mix(rgb, [255, 255, 255], 0.65),
    "300": mix(rgb, [255, 255, 255], 0.45),
    "400": mix(rgb, [255, 255, 255], 0.22),
    "500": mix(rgb, [255, 255, 255], 0.08),
    "600": toHex(rgb),
    "700": mix(rgb, [0, 0, 0], 0.18),
    "800": mix(rgb, [0, 0, 0], 0.32),
    "900": mix(rgb, [0, 0, 0], 0.50),
  };
  const root = document.documentElement;
  for (const [tom, val] of Object.entries(palette)) {
    root.style.setProperty(`--color-${prefixo}-${tom}`, val);
  }
  root.style.setProperty(`--color-${prefixo}`, palette["600"]);
}

function parseHex(hex: string): [number, number, number] | null {
  const m = hex.trim().replace(/^#/, "").match(/^([0-9a-f]{6}|[0-9a-f]{3})$/i);
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function mix(a: [number, number, number], b: [number, number, number], t: number): string {
  const c: [number, number, number] = [
    Math.round(a[0] * (1 - t) + b[0] * t),
    Math.round(a[1] * (1 - t) + b[1] * t),
    Math.round(a[2] * (1 - t) + b[2] * t),
  ];
  return toHex(c);
}

function toHex(c: [number, number, number]): string {
  return "#" + c.map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0")).join("");
}
